// VersioningPanel — entity-agnostic orchestrator.
//
// Composition root for the versioning experience. Receives an `entity`
// (invoice / credit-note / journal) plus a config that describes:
//   • `endpointBase`      — REST prefix (`/invoices` / `/credit-notes` / `/journals`)
//   • `idField`           — name of the id key on the entity object
//   • `entityLabel`       — Arabic label for the header
//   • `actionAmend`       — Action Engine id for the amend gate
//   • `actionAmendPost`   — Action Engine id for the post-amendment gate
//   • `amendableFields[]` — `{key, label, type}` definitions for the modal
//   • `financialFields`   — Set of keys treated as financial in the diff
//   • `headlineFieldOrder`— ordered list for picking the Δ banner headline
//   • `validate(snapshot, original, patch)` — optional client-side check
//   • `testIdPrefix`      — data-testid namespace (cn / inv / jrn)
//   • `accentClass`       — Tailwind colour for the create button
//
// Backend contract is uniform across entities:
//   GET  {endpointBase}/{id}/versions
//   POST {endpointBase}/{id}/amend
//   POST {endpointBase}/{id}/versions/{vid}/post
import React, { useCallback, useEffect, useState } from 'react';
import { History, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { axiosInstance } from '../../App';
import { ActionGatedButton } from '../ActionGate';
import VersionTimeline from './VersionTimeline';
import CreateAmendmentModal from './CreateAmendmentModal';

export default function VersioningPanel({ entity, config, onUpdated }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const entityId = entity?.[config.idField];

  const load = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      const res = await axiosInstance.get(`${config.endpointBase}/${entityId}/versions`);
      setVersions(res.data?.versions || []);
    } catch (err) {
      toast.error('تعذّر تحميل الإصدارات');
    } finally {
      setLoading(false);
    }
  }, [entityId, config.endpointBase]);

  useEffect(() => {
    const handle = setTimeout(load, 0);
    return () => clearTimeout(handle);
  }, [load]);

  const handlePost = async (versionId) => {
    if (!window.confirm('تأكيد ترحيل هذا التعديل؟ سيتم استبدال الإصدار الحالي.')) return;
    try {
      const res = await axiosInstance.post(
        `${config.endpointBase}/${entityId}/versions/${versionId}/post`,
      );
      toast.success('تم الترحيل بنجاح');
      // Each entity returns its updated body under a different key
      // (`invoice` / `credit_note` / `journal`); just hand the whole
      // response data up.
      onUpdated && onUpdated(res.data);
      await load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : detail?.reason || 'تعذّر الترحيل';
      toast.error(msg);
    }
  };

  const accent = config.accentClass || 'bg-violet-600 hover:bg-violet-700';

  return (
    <div
      className="bg-white border border-slate-200 rounded-xl mt-4"
      data-testid={`${config.testIdPrefix}-versions-panel`}
    >
      <div className="flex items-center justify-between p-4 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <History size={16} className="text-slate-500" />
          <span className="font-semibold text-slate-800 text-sm">
            سجل إصدارات {config.entityLabel}
          </span>
          <span className="text-xs text-slate-400">({versions.length})</span>
        </div>
        <ActionGatedButton
          action={config.actionAmend}
          ctx={entity}
          onClick={() => setShowModal(true)}
          data-testid={`${config.testIdPrefix}-create-amendment-btn`}
          className={`px-3 py-1.5 rounded-lg text-white text-xs font-medium disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center gap-1 ${accent}`}
        >
          <Plus size={14} />
          إنشاء تعديل
        </ActionGatedButton>
      </div>

      <div className="p-4">
        {loading ? (
          <div className="space-y-2">
            {[0, 1].map((i) => (
              <div key={i} className="h-12 bg-slate-100 rounded animate-pulse" />
            ))}
          </div>
        ) : (
          <VersionTimeline
            versions={versions}
            config={config}
            onPost={handlePost}
            testIdPrefix={`${config.testIdPrefix}-version`}
          />
        )}
      </div>

      {showModal && (
        <CreateAmendmentModal
          entity={entity}
          config={config}
          onClose={() => setShowModal(false)}
          onCreated={() => load()}
        />
      )}
    </div>
  );
}
